import { CreditCard } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { usePortal, useSubscription } from "@/hooks/use-billing";
import { paymentFailed } from "@/lib/api/billing";
import { useAuth } from "@/lib/auth/auth-context";
import { redirect } from "@/lib/core/browser";

/**
 * Across the app while the last renewal failed: the plan stays during Stripe's retries, and one
 * click opens the billing portal to update the card. Only the subscriber sees it.
 */
export function PaymentFailedBanner() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { data: subscription } = useSubscription(user?.id);
  const portal = usePortal();
  if (!paymentFailed(subscription)) return null;

  async function update() {
    try {
      redirect(await portal.mutateAsync());
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("plans.portalError"));
    }
  }

  return (
    <div
      role="alert"
      className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 border-b border-destructive/30 bg-destructive/10 px-4 py-2 text-sm"
      data-testid="payment-failed-banner"
    >
      <span>
        {subscription?.status === "unpaid"
          ? t("billing.paymentFailed.unpaid")
          : t("billing.paymentFailed.pastDue")}
      </span>
      <Button
        size="sm"
        variant="outline"
        className="h-7"
        onClick={() => void update()}
        disabled={portal.isPending}
        data-testid="payment-failed-update"
      >
        <CreditCard /> {t("billing.paymentFailed.update")}
      </Button>
    </div>
  );
}
